import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TypeAnimation } from 'react-type-animation';
import StarsBackground from './StarsBackground';
import AuthModal from './AuthModal';
import AdEditor from './AdEditor';
import '../App.css';
import './DemoPage.css';

// Aris Logo Component
const ArisLogo = () => (
  <img src="/aris_logo.png" alt="Aris Logo" className="logo" />
);

// Supabase storage base URL
const SUPABASE_BASE_URL = 'https://jipajdmuascmlgatdopp.supabase.co/storage/v1/object/public/generated-ads/ads/';

// Default demo configuration
const DEFAULT_DEMO_IMAGES = [
  'ad_1764092720456_1_0.7431542230076641.png',
  'ad_1764094155718_0_0.6696199461902698.png',
  'ad_1764092834088_1_0.3595524662449461.png',
  'ad_1764093642141_3_0.8183082529244834.png',
  'ad_1764092424555_0_0.48493584127163136.png',
  'ad_1764092423170_1_0.0017341616735015997.png',
  'ad_1764093875187_3_0.244525043703264.png',
  'ad_1764094156881_2_0.20946499062954327.png',
  'ad_1764093153258_3_0.4487642918380669.png',
  'ad_1764093153533_0_0.9329909711546032.png',
  'ad_1764093485477_1_0.845911208132355.png',
  'ad_1764094158636_3_0.7768934457625485.png',
  'ad_1764092422309_2_0.38037777639686754.png',
  'ad_1764092423737_3_0.11368904605719776.png',
  'ad_1764092598831_1_0.5581710897497946.png',
  'ad_1764092579177_2_0.3103920715482281.png',
  'ad_1764092582602_3_0.015877784940742434.png',
  'ad_1764092586100_0_0.9393105455528499.png',
  'ad_1764092836785_0_0.569925236241842.png',
  'ad_1764092834367_3_0.3724075591663317.png',
  'ad_1764093337047_0_0.9904545912092645.png',
  'ad_1764093643664_2_0.49659521678231444.png',
  'ad_1764094018405_1_0.8174822995190206.png',
  'ad_1764094017006_3_0.16158692500777272.png'
];
const DEFAULT_COMPANY = 'Demo';

const DemoPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [imageUrls, setImageUrls] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [showAuthOnEdit, setShowAuthOnEdit] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Get images from URL params (comma-separated)
    const imagesParam = searchParams.get('images');
    const company = searchParams.get('company');
    
    if (imagesParam) {
      const rawUrls = imagesParam.split(',').map(url => {
        const trimmed = url.trim();
        // Decode URL-encoded strings (handles data URLs with special chars)
        try {
          return decodeURIComponent(trimmed);
        } catch (e) {
          // If decoding fails, use as-is
          return trimmed;
        }
      });
      
      // Convert to full URLs if needed
      const fullUrls = rawUrls.map(url => {
        // If it's already a full URL (starts with http) or a data URL, use as-is
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
          return url;
        }
        // Otherwise, it's a filename - prepend Supabase base URL
        return SUPABASE_BASE_URL + url;
      });
      
      setImageUrls(fullUrls);
      setCompanyName(company || DEFAULT_COMPANY);
    } else {
      // No params provided - use default Nike demo
      const defaultUrls = DEFAULT_DEMO_IMAGES.map(filename => SUPABASE_BASE_URL + filename);
      setImageUrls(defaultUrls);
      setCompanyName(company || DEFAULT_COMPANY);
    }
  }, [searchParams]);

  // Automatic slider animation with smooth easing (from home page)
  useEffect(() => {
    let startTime = null;
    let animationFrame;
    const duration = 3000;
    const pauseDuration = 400;
    let isPaused = false;
    let pauseStart = null;
    let goingForward = true;
    
    const easeInOutCubic = (t) => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      
      if (isPaused) {
        if (!pauseStart) pauseStart = timestamp;
        if (timestamp - pauseStart >= pauseDuration) {
          isPaused = false;
          pauseStart = null;
          startTime = timestamp;
          goingForward = !goingForward;
        }
        animationFrame = requestAnimationFrame(animate);
        return;
      }
      
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      
      const position = goingForward ? easedProgress * 55 : 55 - (easedProgress * 55);
      setSliderPosition(position);
      
      if (progress >= 1) {
        isPaused = true;
      }
      
      animationFrame = requestAnimationFrame(animate);
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setShowAuthModal(true);
    }
  };

  const handleEditAd = (url, index) => {
    console.log('Opening edit for URL:', url);
    const ad = {
      id: `demo_ad_${index}`,
      imageUrl: url,
      prompt: `${companyName} Advertisement`,
      aspectRatio: '1:1',
      width: 1024,
      height: 1024,
      caption: '',
      createdAt: new Date().toISOString(),
      model: 'demo'
    };
    console.log('Ad object:', ad);
    setEditingAd(ad);
    setShowAuthOnEdit(true);
  };

  const handleCloseEditor = () => {
    setEditingAd(null);
    setShowAuthOnEdit(false);
  };

  const handleDownload = async (url, index) => {
    try {
      // If it's a data URL, use it directly
      if (url.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${companyName.toLowerCase()}-ad-${index + 1}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      
      // Otherwise, fetch it first
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${companyName.toLowerCase()}-ad-${index + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (imageUrls.length === 0) {
    return null;
  }

  return (
    <div className="App demo-page">
      <StarsBackground />
      
      <div className="app-content">
        {/* Header */}
        <header className="app-header">
          <div className="logo-container" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <ArisLogo />
            <div className="brand-name">Aris</div>
          </div>
          
          <nav className="nav-buttons">
            <a 
              href="https://calendly.com/bruce-kumar-aris/30min" 
              target="_blank" 
              rel="noopener noreferrer"
              className="nav-button book-demo-button"
            >
              Get a demo
            </a>
            <button className="nav-button primary start-button" onClick={() => setShowAuthModal(true)}>
              Start for $0 →
            </button>
          </nav>
        </header>

        {/* Main Content */}
        <main className="main-section">
          {/* Demo Company Header */}
          <div className="demo-company-header">
            <h1 className="demo-company-title">
              Demo <span className="company-highlight">{companyName}</span> Ads with AI
            </h1>
            <p className="demo-company-subtitle">By Aris</p>
          </div>

          {/* Demo Ads Grid */}
          {imageUrls.length > 0 && (
            <div className="results-section">
              <div className="results-grid">
                {imageUrls.map((url, index) => (
                  <div key={index} className="ad-result-wrapper">
                    <div className="ad-image-container" onClick={() => handleEditAd(url, index)}>
                      <img 
                        src={url} 
                        alt={`${companyName} Ad ${index + 1}`}
                        className="ad-image"
                        onLoad={(e) => e.target.classList.add('loaded')}
                      />
                      <div className="ad-hover-overlay">
                        <button className="ad-hover-button edit-button" onClick={(e) => { e.stopPropagation(); handleEditAd(url, index); }}>
                          Edit
                        </button>
                        <button className="ad-hover-button download-icon-button" onClick={(e) => { e.stopPropagation(); handleDownload(url, index); }} title="Download">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Try It Yourself Section */}
          <div className="demo-try-section">
            <h3 className="try-title">Try it yourself</h3>
            <p className="try-subtitle">Type anything and see the magic happen</p>
          </div>

          {/* Chat Input - Same as home page */}
          <div className="chat-input-container">
            <div className="input-wrapper">
              <div className="input-container">
                {!inputValue && (
                  <div className="typing-placeholder">
                    <TypeAnimation
                      sequence={[
                        'Create an ad for my shoe product',
                        2000,
                        'Design an ad for my restaurant in Palo Alto',
                        2000,
                        'Build a creative ad for my agency',
                        2000,
                        'Make an ad for my coffee shop',
                        2000,
                        'Generate an ad for my tech startup',
                        2000,
                        'Create an ad for my fitness app',
                        2000,
                      ]}
                      wrapper="span"
                      speed={50}
                      deletionSpeed={80}
                      repeat={Infinity}
                      cursor={true}
                      style={{ display: 'inline-block' }}
                    />
                  </div>
                )}
                
                <textarea
                  ref={inputRef}
                  className="chat-input"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder=""
                />
              </div>
              
              <div className="input-footer">
                {/* Image Upload Button - Bottom Left */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={() => setShowAuthModal(true)}
                  style={{ display: 'none' }}
                />
                <button
                  className="image-upload-button"
                  onClick={() => setShowAuthModal(true)}
                  type="button"
                  title="Upload images (max 6)"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </button>
                
                <button
                  className="generate-button"
                  onClick={() => setShowAuthModal(true)}
                  disabled={!inputValue.trim()}
                >
                  Generate Ads →
                </button>
              </div>
            </div>
          </div>

          {/* Before/After Demo Section */}
          <section className="demo-section">
            <div className="before-after-container">
              <div className="demo-header">
                <h2 className="demo-title">Edit Any Ad in Seconds</h2>
                <p className="demo-subtitle">Just describe what you want to change</p>
              </div>
              
              <div className="before-after-wrapper">
                {/* Before Image (underneath) */}
                <div className="before-image-container">
                  <img src="/before.png" alt="Before Edit" className="comparison-image" />
                </div>
                
                {/* After Image (overlay with clip) */}
                <div 
                  className="after-image-container" 
                  style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                >
                  <img src="/after.png" alt="After Edit" className="comparison-image" />
                  <div className="image-label after-label">After</div>
                </div>
                
                {/* Slider Handle */}
                <div 
                  className="slider-handle" 
                  style={{ left: `${sliderPosition}%` }}
                >
                  <div className="slider-line"></div>
                  <div className="slider-button">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M15 18l-6-6 6-6"/>
                      <path d="M9 6l6 6-6 6"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="demo-section">
            <div className="demo-section-content">
              <div className="demo-header animated-header">
                <h2 className="demo-title animated-gradient-text">From Website to Ad in One Click</h2>
                <div className="url-bar-animation">
                  <div className="url-bar">
                    <div className="url-dot"></div>
                    <div className="url-dot"></div>
                    <div className="url-dot"></div>
                    <div className="url-text">https://nike.com</div>
                    <div className="url-arrow">→</div>
                  </div>
                </div>
              </div>
              <img src="/nike.png" alt="Ad Variations Demo" className="demo-image" />
            </div>
          </section>
        </main>
      </div>

      {/* Copyright Footer */}
      <footer className="copyright-footer">
        <div className="footer-links">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span className="footer-separator">·</span>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        </div>
        <div className="footer-copyright">
          © {new Date().getFullYear()} Pilot Ventures (Aris). All rights reserved.
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onAuthenticate={() => {
            setShowAuthModal(false);
            navigate('/');
          }}
        />
      )}

      {/* Ad Editor - with auth gate */}
      {editingAd && (
        <>
           <AdEditor
             ad={editingAd}
             onClose={handleCloseEditor}
             onUpdateGenerations={() => {}}
             onSaveEditedAd={() => {}}
             editHistory={null}
             onUpdateEditHistory={() => {}}
             isDemoMode={true}
             onDemoAction={() => {
               setShowAuthModal(true);
               setEditingAd(null);
               setShowAuthOnEdit(false);
             }}
           />
          {showAuthOnEdit && (
            <div 
              className="auth-overlay-gate"
              onClick={() => {
                setShowAuthModal(true);
                setEditingAd(null);
                setShowAuthOnEdit(false);
              }}
            >
              <div className="auth-gate-message">
                <h3>Sign up to edit this ad</h3>
                <p>Click anywhere to get started</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DemoPage;

